import { distanceKm } from "../maps/walk";
import { liveListFor, markerOf, staticMatch } from "../shared/liveList";
import { HOSTELS, type Poi } from "./content";

/**
 * A hostel page's nearby places: the hostel's synced Stippl list (`explore/partner-lists/mad-monkey-<slug>.md`, read by
 * the server loader `madMonkeyHostel`) when it exists and has places, else the static ones in `madMonkeyData.json`. The
 * list holds the hostel itself in its first chapter; the server leaves that chapter out, the hostel is the page's base.
 *
 * Without a list it returns the static array itself, so the page renders what it did before the loader existed. With
 * one, a place the static data already has (by name) keeps its marker, photo and rating; the list gives
 * the order, the coordinates, the line ("why") and the label. See `shared/liveList.ts`.
 */
export function hostelPois(slug: string, data: unknown): Poi[] | undefined {
	const hostel = HOSTELS[slug];
	const fallback = hostel?.pois;
	const live = hostel ? liveListFor(data, slug) : null;
	if (!live || !live.items.length) return fallback;

	return live.items.map((item): Poi => {
		const known = staticMatch(item, fallback ?? []);
		const { grp, icon } = known ?? markerOf(item);
		const photo = known?.photo ? { photo: known.photo } : item.photo ? { photo: item.photo, ...(item.credit ? { credit: item.credit } : {}) } : {};
		return {
			name: known?.name ?? item.name,
			grp,
			icon,
			cat: known?.cat ?? item.attraction ?? "",
			// the Google place id "Get this trip" saves the place under; "" only for a place neither side has one for
			gid: item.googlePlaceId ?? known?.gid ?? "",
			lat: item.lat,
			lon: item.lng,
			why: item.line || known?.why || "",
			label: item.label ?? known?.label ?? "",
			// straight-line km from the hostel to 100 m, exactly how every static `dist` was made
			dist: Math.round(distanceKm([hostel.lon, hostel.lat], [item.lng, item.lat]) * 10) / 10,
			...(known?.rating !== undefined ? { rating: known.rating } : {}),
			...photo,
		};
	});
}
